import * as React from "react"
import { Clock } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const QUARTER_MINUTES = [0, 15, 30, 45] as const

export type TimePickerMinuteGranularity = "quarter" | "minute"

function parseTime(
  value: string,
  allowHour24MidnightEnd?: boolean,
): { hour: number; minute: number } | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (allowHour24MidnightEnd && trimmed === "24:00") {
    return { hour: 24, minute: 0 }
  }
  const match = /^(\d{1,2}):(\d{2})$/.exec(trimmed)
  if (!match) return null
  const hour = Number(match[1])
  const minute = Number(match[2])
  const maxHour = allowHour24MidnightEnd ? 24 : 23
  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > maxHour ||
    minute < 0 ||
    minute > 59
  ) {
    return null
  }
  if (hour === 24 && minute !== 0) return null
  return { hour, minute }
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0")
}

function formatTime(hour: number, minute: number): string {
  return `${pad2(hour)}:${pad2(minute)}`
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const HOURS_WITH_MIDNIGHT_END = [...HOURS, 24] as const
const MINUTES = Array.from({ length: 60 }, (_, i) => i)

export interface TimePickerProps {
  /** Empty string clears; otherwise `HH:mm` (24h). */
  value: string
  onChange: (value: string) => void
  /** `quarter`: 00, 15, 30, 45. `minute`: 0–59. */
  minuteGranularity?: TimePickerMinuteGranularity
  /**
   * When true, hour `24` and value `24:00` are allowed (exclusive end of calendar day).
   * Minute is fixed at `:00` for hour 24.
   */
  allowEndOfDayMidnight?: boolean
  disabled?: boolean
  id?: string
  placeholder?: string
  className?: string
  onFocus?: React.FocusEventHandler<HTMLButtonElement>
  onBlur?: React.FocusEventHandler<HTMLButtonElement>
}

export function TimePicker({
  value,
  onChange,
  minuteGranularity = "minute",
  allowEndOfDayMidnight = false,
  disabled = false,
  id,
  placeholder = "Select time",
  className,
  onFocus,
  onBlur,
}: TimePickerProps) {
  const parsed = parseTime(value, allowEndOfDayMidnight)
  const hour = parsed?.hour ?? 0
  const minute = parsed?.minute ?? 0
  const hourOptions = allowEndOfDayMidnight ? HOURS_WITH_MIDNIGHT_END : HOURS

  const setHour = React.useCallback(
    (hourStr: string) => {
      const h = Number(hourStr)
      if (allowEndOfDayMidnight && h === 24) {
        onChange("24:00")
        return
      }
      const m = parseTime(value, allowEndOfDayMidnight)?.minute ?? 0
      onChange(formatTime(h, m))
    },
    [allowEndOfDayMidnight, onChange, value]
  )

  const setMinute = React.useCallback(
    (minuteStr: string) => {
      const m = Number(minuteStr)
      const h = parseTime(value, allowEndOfDayMidnight)?.hour ?? 0
      if (allowEndOfDayMidnight && h === 24) return
      onChange(formatTime(h, m))
    },
    [allowEndOfDayMidnight, onChange, value]
  )

  const displayLabel = parsed ? formatTime(hour, minute) : null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          onFocus={onFocus}
          onBlur={onBlur}
          className={cn(
            "w-full justify-start text-left font-normal",
            !displayLabel && "text-muted-foreground",
            className
          )}
        >
          <Clock className="mr-2 h-4 w-4 shrink-0 opacity-60" />
          {displayLabel ?? placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-2">
            <div className="grid flex-1 gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                Hour (24h)
              </span>
              <Select
                value={parsed ? String(hour) : undefined}
                onValueChange={setHour}
                disabled={disabled}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent position="popper" className="max-h-60">
                  {hourOptions.map((h) => (
                    <SelectItem key={h} value={String(h)}>
                      {h === 24 || h === 0 ? "24 (midnight)" : pad2(h)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid flex-1 gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                {minuteGranularity === "quarter"
                  ? "Minutes (15 min)"
                  : "Minutes"}
              </span>
              {minuteGranularity === "quarter" ? (
                <div className="grid grid-cols-2 gap-1.5">
                  {QUARTER_MINUTES.map((m) => (
                    <Button
                      key={m}
                      type="button"
                      size="sm"
                      variant={minute === m ? "default" : "outline"}
                      className="h-9 px-2 font-mono text-xs tabular-nums"
                      disabled={disabled}
                      onClick={() => onChange(formatTime(hour, m))}
                    >
                      :{pad2(m)}
                    </Button>
                  ))}
                </div>
              ) : (
                <Select
                  value={parsed ? String(minute) : undefined}
                  onValueChange={setMinute}
                  disabled={disabled || (allowEndOfDayMidnight && hour === 24)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-60">
                    {MINUTES.map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {pad2(m)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="flex justify-end border-t border-border pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              disabled={disabled || !parsed}
              onClick={() => onChange("")}
            >
              Clear
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { parseTime as parseTimePickerValue, formatTime as formatTimePickerValue }
